/**
 * Pure interaction state machine for the "Confidential Project" warning banner.
 *
 * When a user checks the confidential checkbox we show an inline warning banner and
 * block the Next Step button until they explicitly confirm or back out. This reducer
 * models that interaction so the component stays declarative and the logic stays
 * unit-testable without a DOM.
 *
 * `isConfidential` mirrors the value persisted via react-hook-form; the component is
 * responsible for syncing each transition's `isConfidential` back into the form.
 */
export type ConfidentialWarningState = {
  isConfidential: boolean;
  showWarning: boolean;
};

export type ConfidentialWarningAction =
  // User toggled the checkbox directly. `checked` is the new checkbox value.
  | { type: 'toggle'; checked: boolean }
  // User clicked "Yes, this must be confidential".
  | { type: 'confirm' }
  // User clicked "Remove confidential flag".
  | { type: 'removeFlag' };

export function confidentialWarningReducer(
  state: ConfidentialWarningState,
  action: ConfidentialWarningAction,
): ConfidentialWarningState {
  switch (action.type) {
    case 'toggle':
      // Checking shows the warning; unchecking (incl. while the banner is open)
      // clears the flag and dismisses the banner.
      return { isConfidential: action.checked, showWarning: action.checked };
    case 'confirm':
      return { isConfidential: true, showWarning: false };
    case 'removeFlag':
      return { isConfidential: false, showWarning: false };
    default:
      return state;
  }
}
